import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import { GraduationCap, Loader2, ExternalLink, BadgeCheck, Wallet, CalendarClock, Search } from "lucide-react";

const STREAMS = ["Any", "Science", "Commerce", "Humanities", "Other"];
const LEVELS = ["Any", "Class 11", "Class 12", "Graduate"];
const BUDGETS = ["Under ₹1 Lakh", "₹1–5 Lakh", "₹5–15 Lakh", "₹15 Lakh+", "Need scholarships"];

export default function ScholarshipsPage() {
  const [stream, setStream] = useState("Science");
  const [level, setLevel] = useState("Class 12");
  const [budget, setBudget] = useState("Need scholarships");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const find = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/scholarships/match", {
        stream, budget, level: level === "Any" ? null : level,
      });
      setData(data);
      if (data.matches.length === 0) toast.info("No matches found for these filters.");
    } catch (e) {
      toast.error("Could not fetch scholarships.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-8 py-8">
      <Header icon={GraduationCap} eyebrow="Scholarship Finder"
        title="Find scholarships that fund your future" desc="Smart-matched to your stream, class and budget." />

      <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6 grid sm:grid-cols-3 gap-5">
        <div>
          <label className="text-sm font-medium text-slate-300">Stream</label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="scholarship-stream-select">
            {STREAMS.map((s) => (
              <button key={s} onClick={() => setStream(s)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${stream === s ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"}`}>{s}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-300">Level</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {LEVELS.map((l) => (
              <button key={l} onClick={() => setLevel(l)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${level === l ? "bg-indigo-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-300">Budget / Income</label>
          <select data-testid="scholarship-budget-select" value={budget} onChange={(e) => setBudget(e.target.value)}
            className="mt-2 w-full rounded-xl border border-white/10 bg-[#0F1626] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50">
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="sm:col-span-3">
          <button data-testid="scholarship-find-btn" onClick={find} disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 text-sm font-bold text-[#041016] transition-transform hover:scale-105 disabled:opacity-50">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Matching...</> : <><Search className="h-4 w-4" /> Find Scholarships</>}
          </button>
        </div>
      </div>

      {data && (
        <div className="mt-8">
          <p className="mb-4 text-sm text-slate-400">
            <span className="font-semibold text-emerald-300">{data.eligible_count}</span> of {data.matches.length} scholarships match your profile
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.matches.map((s, i) => (
              <motion.div key={s.name} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                data-testid="scholarship-card"
                className={`rounded-2xl border p-5 ${s.eligible ? "border-emerald-400/25 bg-emerald-400/[0.04]" : "border-white/8 bg-[#131C2E]"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display font-semibold text-white">{s.name}</p>
                    <p className="text-xs text-slate-400">{s.provider}</p>
                  </div>
                  {s.eligible ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/15 px-2.5 py-1 text-[10px] font-bold text-emerald-300 shrink-0">
                      <BadgeCheck className="h-3 w-3" /> {s.match_score}% match
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-slate-600/40 px-2.5 py-1 text-[10px] font-bold text-slate-300 shrink-0">
                      Income above limit
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-slate-400">{s.desc}</p>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-300">
                  <span className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5 text-cyan-400" /> ₹{(s.amount / 1000).toFixed(0)}K / year</span>
                  <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5 text-amber-400" /> {s.deadline}</span>
                  <span className="rounded-full bg-white/5 px-2 py-0.5">{s.category}</span>
                </div>
                <p className="mt-2 text-[11px] text-slate-400 italic">Why: {s.match_reason}</p>
                <a href={s.link} target="_blank" rel="noreferrer" data-testid="scholarship-apply-link"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-cyan-400">
                  Apply now <ExternalLink className="h-3 w-3" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
